import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';

const locations = [
  {
    name: 'Kirkwood',
    tagline: 'Ski-In / Ski-Out Luxury',
    county: 'Alpine County, CA',
    desc: 'Kirkwood is one of California\'s premier ski resorts, offering direct slope access, dramatic mountain scenery, and a strictly limited supply of high-value properties. Its remote, pristine setting attracts buyers seeking both lifestyle excellence and long-term investment potential in the Sierra Nevada.',
    highlights: ['World-class ski resort direct access', 'Limited inventory — premium values', 'Strong seasonal rental demand', 'Alpine County exclusivity'],
    image:
      'https://public.readdy.ai/ai/img_res/618a0784d91e79b1b460ec390d233cc8.jpg',
  },
  {
    name: 'South Lake Tahoe',
    tagline: 'Lakefront & Mountain Living',
    county: 'El Dorado County, CA',
    desc: 'South Lake Tahoe offers a diverse range of luxury properties from lakefront estates to mountain retreats. The vibrant year-round community and unmatched natural beauty make it one of the most sought-after second-home destinations in the Western United States.',
    highlights: ['Lakefront estate opportunities', 'Year-round lifestyle market', 'Strong vacation rental potential', 'El Dorado County location'],
    image:
      'https://readdy.ai/api/search-image?query=South%20Lake%20Tahoe%20California%20luxury%20lakefront%20estate%20private%20dock%20crystal%20clear%20blue%20water%20Sierra%20Nevada%20mountains%20reflection%20golden%20sunset%20high%20end%20real%20estate%20photography%20premium%20property&width=900&height=600&seq=loc2-ck&orientation=landscape',
  },
  {
    name: 'Alpine County',
    tagline: 'Rare & Remote Alpine Estates',
    county: 'Alpine County, CA',
    desc: 'Alpine County is one of the least populated counties in California, offering extraordinary privacy, breathtaking scenery, and some of the most exclusive real estate in the Sierra Nevada. Properties here represent a unique convergence of lifestyle, investment, and legacy value.',
    highlights: ['Extreme privacy and exclusivity', 'Lowest density county in CA', 'Gateway to Kirkwood Resort', 'Legacy property opportunities'],
    image:
      'https://readdy.ai/api/search-image?query=Alpine%20County%20California%20remote%20mountain%20luxury%20estate%20pristine%20wilderness%20Sierra%20Nevada%20dramatic%20rugged%20landscape%20exclusive%20privacy%20real%20estate%20meadow%20pine%20forest%20photorealistic&width=900&height=600&seq=loc3-ck&orientation=landscape',
  },
  {
    name: 'El Dorado County',
    tagline: 'Mountain Communities & Retreats',
    county: 'El Dorado County, CA',
    desc: 'El Dorado County encompasses a wide range of mountain communities, from lakeside vacation properties to rural acreage estates. Its proximity to South Lake Tahoe and Sacramento creates a compelling blend of accessibility and alpine living.',
    highlights: ['Broad price range and property types', 'Strong highway access to the Bay Area', 'Diverse community character', 'Growing luxury market segment'],
    image:
      'https://readdy.ai/api/search-image?query=El%20Dorado%20County%20California%20mountain%20luxury%20retreat%20property%20pine%20forest%20meadow%20Sierra%20Nevada%20foothills%20rural%20estate%20acreage%20premium%20real%20estate%20photography%20warm%20afternoon%20light&width=900&height=600&seq=loc4-ck&orientation=landscape',
  },
];

const testimonials = [
  { quote: 'Cam knew every property in the area — found us something off-market that we never would have discovered alone.', author: 'L. & B. Ashworth', location: 'North Lake Tahoe, CA', stars: 5 },
  { quote: 'South Lake Tahoe real estate is complex and seasonal — Cam\'s guidance made the buying process clear and stress-free.', author: 'M. Torres', location: 'South Lake Tahoe, CA', stars: 5 },
  { quote: 'We sold our Alpine County property above list price with Cam\'s strategic positioning. Remarkable results.', author: 'P. & D. Sutton', location: 'Alpine County, CA', stars: 5 },
];

const faqs = [
  { q: 'What areas does Cam Kirkwood serve in Lake Tahoe?', a: 'Cam serves South Lake Tahoe, North Lake Tahoe, Alpine County, El Dorado County, and the broader Sierra Nevada alpine corridor. With deep local roots across all these communities, Cam provides intimate market knowledge for luxury buyers and sellers at every price point.' },
  { q: 'What is the best area to buy a second home near Lake Tahoe?', a: 'North Lake Tahoe and Incline Village are ideal for prestige and lake views. South Lake Tahoe offers lakefront estates and year-round lifestyle. Alpine County provides rare privacy and exclusivity. El Dorado County gives accessible mountain living. Cam helps match you to the right area for your goals.' },
  { q: 'How are North and South Lake Tahoe real estate markets different?', a: 'North Lake Tahoe, including Incline Village, tends toward higher price points with a more exclusive buyer profile. South Lake Tahoe offers greater diversity in property types and price ranges. Both markets require deep local knowledge to navigate — which Cam Kirkwood provides across the entire Tahoe region.' },
];

export default function LocationsPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <div className="font-sans bg-warm-white overflow-x-hidden">
      <Header />

      {/* 1. HERO */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              'url(https://readdy.ai/api/search-image?query=aerial%20panoramic%20view%20Lake%20Tahoe%20Kirkwood%20California%20vast%20alpine%20landscape%20Sierra%20Nevada%20mountains%20multiple%20luxury%20properties%20pristine%20wilderness%20dramatic%20golden%20hour%20cinematic%20wide%20angle%20photography%20photorealistic&width=1920&height=900&seq=loc-hero-ck&orientation=landscape)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-charcoal/75 via-charcoal/55 to-charcoal/80" />
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-warm-white to-transparent z-10" />
        <div className="relative z-20 max-w-4xl mx-auto text-center px-4 md:px-8 pt-28 pb-16">
          <p className="text-[10px] tracking-[0.5em] uppercase text-gold mb-5">Where We Work</p>
          <h1 className="font-serif text-5xl md:text-7xl text-white font-light leading-tight mb-6">
            <em className="italic text-gold">Lake Tahoe</em> &amp; Sierra Nevada
            <span className="block text-3xl md:text-4xl mt-4 font-light text-white/75">Real Estate</span>
          </h1>
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="w-12 h-px bg-gold/40" />
            <div className="w-1.5 h-1.5 rotate-45 bg-gold/50" />
            <div className="w-12 h-px bg-gold/40" />
          </div>
          <p className="text-warm-white/65 max-w-xl mx-auto text-base leading-relaxed">
            Local market insights &bull; Property types &bull; Lifestyle benefits &bull; Buyer/seller guidance
          </p>
        </div>
      </section>

      {/* 2. TRUST BAR */}
      <section className="bg-charcoal border-y border-gold/15 py-5 overflow-hidden">
        <div className="flex items-center trust-marquee-loc">
          {['South Lake Tahoe', 'North Lake Tahoe', 'Alpine County', 'El Dorado County', 'Sierra Nevada', 'Resort Property Specialists', '★★★★★ Trusted Locally', 'South Lake Tahoe', 'North Lake Tahoe', 'Alpine County', 'El Dorado County', 'Sierra Nevada', 'Resort Property Specialists', '★★★★★ Trusted Locally'].map((item, i) => (
            <span key={i} className="flex items-center gap-6 whitespace-nowrap text-warm-white/60 text-[10px] tracking-[0.25em] uppercase font-light px-8">
              {item}<span className="text-gold/40 text-xs">◆</span>
            </span>
          ))}
        </div>
        <style>{`.trust-marquee-loc { animation: marquee 28s linear infinite; } @keyframes marquee { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }`}</style>
      </section>

      {/* 3. LOCATION CARDS */}
      <section className="py-24 px-4 md:px-8 bg-warm-white rounded-t-[2rem] -mt-8 relative z-10">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[10px] tracking-[0.5em] uppercase text-gold mb-4">Market Profiles</p>
            <h2 className="font-serif text-4xl md:text-5xl text-charcoal font-light">Lake Tahoe <em className="italic">Real Estate Markets</em></h2>
          </div>
          <div className="space-y-20">
            {locations.map((loc, i) => (
              <div key={loc.name} className={`flex flex-col ${i % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-12 items-center`}>
                <div className="lg:w-1/2">
                  <div className="relative rounded-2xl overflow-hidden h-96 w-full" style={{ boxShadow: '0 8px 32px rgba(42,42,42,0.10)' }}>
                    <img src={loc.image} alt={`Real estate in ${loc.name} — Lake Tahoe and Sierra Nevada`} className="w-full h-full object-cover object-top" />
                    <div className="absolute inset-0 bg-gradient-to-t from-charcoal/65 to-transparent" />
                    <div className="absolute bottom-6 left-6">
                      <p className="text-[9px] tracking-[0.3em] uppercase text-gold/80 mb-1">{loc.tagline}</p>
                      <p className="font-serif text-2xl text-white font-light">{loc.name}</p>
                      <p className="text-white/50 text-[10px] tracking-wider mt-0.5">{loc.county}</p>
                    </div>
                  </div>
                </div>
                <div className="lg:w-1/2">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-10 h-px bg-gold/50" />
                    <p className="text-[9px] tracking-[0.4em] uppercase text-gold">Market Profile</p>
                  </div>
                  <h2 className="font-serif text-3xl md:text-4xl text-charcoal font-light mb-5">
                    Real Estate in <em className="italic">{loc.name}</em>
                  </h2>
                  <p className="text-stone leading-relaxed mb-7 text-sm">{loc.desc}</p>
                  <ul className="space-y-2.5 mb-8">
                    {loc.highlights.map((h) => (
                      <li key={h} className="flex items-center gap-3 text-sm text-charcoal">
                        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                          <i className="ri-checkbox-circle-line text-gold text-base" />
                        </div>
                        {h}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/#consultation"
                    className="inline-flex items-center gap-3 whitespace-nowrap px-7 py-3.5 border border-charcoal text-charcoal text-xs tracking-[0.2em] uppercase font-medium cursor-pointer rounded-md hover:bg-charcoal hover:text-warm-white transition-all duration-300"
                  >
                    <span>Explore {loc.name} Properties</span>
                    <i className="ri-arrow-right-line text-gold" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mid CTA */}
      <section className="bg-gold py-10 px-4 md:px-8 relative z-20">
        <div className="max-w-4xl mx-auto text-center flex flex-col sm:flex-row items-center justify-between gap-6">
          <p className="font-serif text-2xl text-white font-light">Find Your Perfect Lake Tahoe Property.</p>
          <a href="#consultation" className="whitespace-nowrap px-7 py-4 bg-charcoal text-white text-xs tracking-[0.2em] uppercase font-medium cursor-pointer rounded-md hover:bg-charcoal/85 transition-all duration-300">
            Start Your Search
          </a>
        </div>
      </section>

      {/* 4. AI BLOCK */}
      <section className="py-24 px-4 md:px-8 bg-charcoal relative z-10">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-[10px] tracking-[0.5em] uppercase text-gold mb-4">Location Insights</p>
            <h2 className="font-serif text-4xl text-white font-light">Lake Tahoe &amp; <em className="italic text-gold">Sierra Nevada Answers</em></h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { q: 'What makes North Lake Tahoe real estate unique?', a: 'North Lake Tahoe, including Incline Village and Tahoe City, offers some of the most prestigious addresses in the Sierra Nevada. Stunning lake views, ski resort proximity, and a discerning buyer demographic create strong demand and long-term appreciation for luxury properties in this coveted alpine market.' },
              { q: 'Is Lake Tahoe real estate a good investment?', a: 'Lake Tahoe has historically shown strong appreciation driven by limited land supply, growing buyer demand, and exceptional lifestyle value. South Lake Tahoe\'s vacation rental market adds additional income potential, while Alpine County provides exceptional privacy and legacy asset quality for long-term investors.' },
              { q: 'What property types are available in South Lake Tahoe?', a: 'South Lake Tahoe offers lakefront estates, mountain retreats, resort-adjacent condominiums, and investment properties with vacation rental income potential. The market ranges from mid-range cabins to multi-million dollar lakefront compounds, giving buyers a wide spectrum of Lake Tahoe real estate options.' },
              { q: 'How does seasonal demand affect Tahoe pricing?', a: 'Lake Tahoe experiences peak buyer activity during ski season and summer. Well-priced properties in prime locations can sell quickly year-round. Cam Kirkwood\'s deep market timing expertise helps clients buy and sell at optimal moments across the entire Lake Tahoe and Sierra Nevada cycle.' },
            ].map((item, i) => (
              <div key={i} className="rounded-xl p-6 border border-white/8 bg-white/3">
                <p className="font-serif text-base text-gold font-medium mb-3">{item.q}</p>
                <p className="text-warm-white/60 text-sm leading-relaxed">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 5. PROCESS */}
      <section className="py-20 px-4 md:px-8 bg-charcoal border-t border-gold/10">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="font-serif text-4xl text-white font-light">The <em className="italic text-gold">Process</em></h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {['Consultation', 'Strategy', 'Execution', 'Closing'].map((step, i) => (
              <div key={step} className="text-center">
                <span className="font-serif text-5xl text-gold/15 block mb-3 font-light">{String(i + 1).padStart(2, '0')}</span>
                <p className="text-warm-white text-sm tracking-wider font-medium">{step}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 6. WHY CHOOSE US */}
      <section className="py-20 px-4 md:px-8 bg-warm-white rounded-t-[2rem] -mt-4 relative z-10">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-[10px] tracking-[0.5em] uppercase text-gold mb-4">Why Us</p>
            <h2 className="font-serif text-4xl text-charcoal font-light">Local <em className="italic">Market Expertise</em></h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: 'ri-map-pin-2-line', title: 'Deep Local Knowledge', text: 'Lake Tahoe and Sierra Nevada market expert with 20+ years of transaction experience.' },
              { icon: 'ri-home-4-line', title: 'Resort Property Specialist', text: 'Expertise in ski-in/ski-out, lakefront, and alpine resort property dynamics.' },
              { icon: 'ri-funds-line', title: 'Investment Analysis', text: 'Data-backed investment guidance for Lake Tahoe and Sierra Nevada real estate portfolios.' },
              { icon: 'ri-vip-crown-line', title: 'Concierge Experience', text: 'White-glove service tailored to luxury buyers and sellers at every step.' },
            ].map((r) => (
              <div key={r.title} className="bg-cream rounded-xl p-6 border border-cream/80">
                <div className="w-10 h-10 flex items-center justify-center bg-gold/10 rounded-lg mb-4 border border-gold/15">
                  <i className={`${r.icon} text-gold text-lg`} />
                </div>
                <h3 className="font-serif text-base text-charcoal font-medium mb-2">{r.title}</h3>
                <p className="text-stone text-sm leading-relaxed">{r.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 7. SERVICE AREAS — Map */}
      <section className="py-20 px-4 md:px-8 bg-cream">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-[10px] tracking-[0.5em] uppercase text-gold mb-3">Regional Coverage</p>
            <h2 className="font-serif text-4xl text-charcoal font-light">Service <em className="italic">Territory</em></h2>
          </div>
          <div className="rounded-2xl overflow-hidden border border-gold/10 h-80 md:h-[28rem]" style={{ boxShadow: '0 8px 40px rgba(42,42,42,0.08)' }}>
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d200000!2d-120.0651!3d38.9338!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e0!3m2!1sen!2sus!4v1711000000000!5m2!1sen!2sus"
              className="w-full h-full"
              title="Kirkwood and Lake Tahoe Region Real Estate Service Area"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>

      {/* 8. TESTIMONIALS */}
      <section className="py-24 px-4 md:px-8 bg-charcoal">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-[10px] tracking-[0.5em] uppercase text-gold mb-4">Client Voices</p>
            <h2 className="font-serif text-4xl text-white font-light">What <em className="italic text-gold">Clients Say</em></h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <div key={i} className="bg-taupe rounded-2xl p-7 border border-white/5">
                <div className="flex gap-1 mb-4">
                  {Array.from({ length: t.stars }).map((_, si) => <i key={si} className="ri-star-fill text-gold text-sm" />)}
                </div>
                <i className="ri-double-quotes-l text-gold/25 text-4xl block mb-3" />
                <p className="text-warm-white/65 text-sm leading-relaxed mb-6 italic">{t.quote}</p>
                <div className="border-t border-white/10 pt-4">
                  <p className="font-serif text-white font-medium">{t.author}</p>
                  <p className="text-gold/60 text-xs mt-0.5">{t.location}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 9. FAQ */}
      <section className="py-24 px-4 md:px-8 bg-warm-white rounded-t-[2rem] -mt-4 relative z-10">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-[10px] tracking-[0.5em] uppercase text-gold mb-3">Questions</p>
            <h2 className="font-serif text-4xl text-charcoal font-light">Location <em className="italic">FAQs</em></h2>
          </div>
          <div className="space-y-3">
            {faqs.map((f, i) => (
              <div key={i} className="rounded-xl overflow-hidden border border-cream bg-white">
                <button className="w-full flex justify-between items-start px-6 py-5 text-left cursor-pointer gap-4" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  <span className="font-serif text-lg text-charcoal font-medium leading-snug">{f.q}</span>
                  <span className={`flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-full border mt-0.5 transition-all duration-300 ${openFaq === i ? 'border-gold bg-gold text-white rotate-45' : 'border-stone/25 text-stone'}`}>
                    <i className="ri-add-line text-sm" />
                  </span>
                </button>
                <div className={`overflow-hidden transition-all duration-400 ${openFaq === i ? 'max-h-48' : 'max-h-0'}`}>
                  <p className="px-6 pb-5 text-stone text-sm leading-relaxed">{f.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 10. FINAL CTA */}
      <section id="consultation" className="relative py-20 px-4 md:px-8 bg-charcoal text-center overflow-hidden">
        <div className="relative z-10 max-w-2xl mx-auto">
          <p className="text-[10px] tracking-[0.5em] uppercase text-gold mb-4">Find Your Property</p>
          <h2 className="font-serif text-4xl md:text-5xl text-white font-light mb-5">
            Explore <em className="italic text-gold">Kirkwood &amp; Tahoe</em>
          </h2>
          <p className="text-warm-white/60 mb-10 leading-relaxed">
            Ready to find your perfect property in this remarkable alpine market? Let&apos;s connect today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:5617146589" className="whitespace-nowrap px-8 py-4 border border-warm-white/25 text-warm-white text-xs tracking-[0.2em] uppercase cursor-pointer rounded-md hover:border-gold hover:text-gold transition-all duration-300">
              <i className="ri-phone-line mr-2 text-gold" />Call 561-714-6589
            </a>
            <Link to="/#consultation" className="whitespace-nowrap px-8 py-4 bg-gold text-white text-xs tracking-[0.2em] uppercase cursor-pointer rounded-md hover:bg-[#b8983a] transition-colors duration-300">
              Schedule Consultation
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}